/**
 * SVG ring gauge for displaying a single percentage value.
 */

import { cn } from "@/lib/utils";

type CircularProgressProps = {
  value: number; // 0–100
  size?: number;
  strokeWidth?: number;
  className?: string;
  label?: string;
};

function colorForValue(value: number): string {
  if (value >= 90) return "text-red-500";
  if (value >= 70) return "text-yellow-500";
  return "text-green-500";
}

export function CircularProgress({
  value,
  size = 80,
  strokeWidth = 8,
  className,
  label,
}: CircularProgressProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className={cn("inline-flex flex-col items-center gap-1", className)}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/30"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={colorForValue(clamped)}
        />
      </svg>
      <span className="text-lg font-semibold">{Math.round(clamped)}%</span>
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
    </div>
  );
}
